import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { SheerIdMilitaryVerificationForm } from 'Forms/SheerIdMilitaryVerificationForm/SheerIdMilitaryVerificationForm';
import {
  getOrganizations,
  submitActiveMilitaryInfo,
  submitInactiveMilitaryInfo,
  getMemberVerify,
} from 'Api/SheerIdService';
import { SheerIdFinePrint } from 'Components/SheerIdFinePrint';
import BangoBackButton from 'Components/BangoBackButton/BangoBackButton';
import { onSubmitReactFormError } from 'Utils/FormValidation';
import {
  SHEER_PAGE_STATUS,
  SHEER_PAGE_TITLES,
  SHEER_PAGE_TYPES,
  SHEER_PAGE_ERROR_MESSAGES,
} from 'Utils/SheerIdHelpers';
import { associateBangoSubscription } from 'Api/SheerIdService';
import { VETERAN_STATUS } from 'Utils/Constants';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { getSheerIdType } from 'Utils/SheerIdHelpers';
import { SHEER_MILITARY_TYPE } from 'Utils/Constants';
import './SheerIdMilitaryVerificationPage.css';

export const SheerIdMilitaryVerificationPage = ({ setPage, setTitle }) => {
  const [mountCount, setMountCount] = useState(1);
  const [initialValues, setInitialValues] = useState({});
  const [organizations, setOrganizations] = useState([]);
  const [formError, setFormError] = useState('');
  const { email } = useSelector((s) => s.user);
  const { subscriptionId, redirectUrl } = useSelector((s) => s.integrations.bango);
  const sheerIdType = getSheerIdType();

  useEffect(() => {
    const fetchOrgs = async () => {
      const { organizations } = await getOrganizations();
      setOrganizations(organizations);
    };

    fetchOrgs();
    setTitle(SHEER_PAGE_TITLES[SHEER_PAGE_TYPES.MILITARY_FORM]);
    trackEvent(TRACKING_ENUMS.EVENT_STATUS_VERIFICATION_STARTED, {
      page_partner: subscriptionId ? 'bango' : 'fox',
      page_program: sheerIdType || SHEER_MILITARY_TYPE,
    });
  }, [setTitle, subscriptionId, sheerIdType]);

  useEffect(() => {
    const associateSubscription = async () => {
      if (subscriptionId && redirectUrl) {
        await associateBangoSubscription(subscriptionId, redirectUrl);
      }
    };

    associateSubscription();
  }, [subscriptionId, redirectUrl]);

  const getOrgName = (id) => {
    const org = organizations.find((org) => org.id === id);
    return org?.name;
  };

  const onFormSubmit = async ({
    status,
    dobDay,
    dobMonth,
    dobYear,
    dischargeDay,
    dischargeMonth,
    dischargeYear,
    firstName,
    lastName,
    organization,
  }) => {
    const organizationId = Number(organization);

    const payload = {
      data: {
        email,
        firstName,
        lastName,
        birthDate: `${dobYear}-${dobMonth}-${String(dobDay).padStart(2, '0')}`,
        organization: {
          id: organizationId,
          name: getOrgName(organizationId),
        },
      },
      subscriptionRequestId: subscriptionId,
    };

    const goToNextStep = (response) => {
      if (response?.systemErrorMessage) {
        setFormError(response.systemErrorMessage);
        onSubmitReactFormError(response.systemErrorMessage);
      } else if (response?.errorIds?.length) {
        setFormError(SHEER_PAGE_ERROR_MESSAGES[response.errorIds[0]]);
        onSubmitReactFormError(SHEER_PAGE_ERROR_MESSAGES[response.errorIds[0]]);
      } else if (response?.errorMessage) {
        setFormError(response.errorMessage);
        return onSubmitReactFormError(response.errorMessage);
      } else {
        setPage(SHEER_PAGE_STATUS[response?.currentStep], response);
      }
    };

    try {
      if (status === VETERAN_STATUS) {
        payload.data.dischargeDate = `${dischargeYear}-${dischargeMonth}-${String(dischargeDay).padStart(2, '0')}`;
        payload['sub-segment'] = 'inactiveMilitary';
        const formResult = await submitInactiveMilitaryInfo(payload);
        const response = await getMemberVerify();
        goToNextStep(formResult?.errorIds ? formResult : response);
      } else {
        payload['sub-segment'] = 'activeMilitary';
        const formResult = await submitActiveMilitaryInfo(payload);
        const response = await getMemberVerify();
        goToNextStep(formResult?.errorIds ? formResult : response);
      }
    } catch (error) {
      goToNextStep(error);
    }
  };

  const remount = (fieldValues, clearDischargeDate, clearOrg) => {
    let finalValues = fieldValues;
    if (clearDischargeDate) {
      const { dischargeDay, dischargeMonth, dischargeYear, ...nonDischargeFields } = finalValues;
      finalValues = nonDischargeFields;
    }
    if (clearOrg) {
      const { organization, ...nonOrgFields } = finalValues;
      finalValues = nonOrgFields;
    }

    setInitialValues(finalValues);
    setMountCount((s) => s + 1);
  };

  return (
    <div className="sheerIdMilitaryVerificationPage">
      <BangoBackButton />
      <div className="militaryTitle">
        Next, we must confirm your active military or veteran status. Enter your information as shown on your military
        documentation.
      </div>
      <SheerIdMilitaryVerificationForm
        key={mountCount}
        remount={remount}
        initialValues={initialValues}
        onFormSubmit={onFormSubmit}
        organizations={organizations}
        error={formError}
      />
      <SheerIdFinePrint />
    </div>
  );
};
